/**
 * Phase 2 — Insurance vertical CategoryConfig.
 *
 * Single source of truth for everything insurance-specific that used
 * to live scattered across PartnerRateTable.jsx (CATEGORY_CONFIG,
 * CATEGORY_BENCHMARKS, SCORE_FIELDS_BY_CATEGORY), RatesPanel.jsx
 * (DETAILS_FIELDS, ADMIN_CATEGORY), and categoryCopy.js
 * (FALLBACK_FUNNEL_COPY).
 *
 * The constants here are the canonical values. Legacy maps in the
 * old files now import from this module so any future drift is
 * caught at the IDE / refactor level.
 */
import { CTA_INSURANCE } from "../../lib/ctaVocabulary";

export const INSURANCE_CONFIG = {
  id: "insurance",
  label: "Auto Insurance",
  singularProviderLabel: "carrier",
  pluralProviderLabel: "carriers",
  providerType: "carrier",

  // Public rate-table contract (was PartnerRateTable.CATEGORY_CONFIG.insurance)
  rateTable: {
    sortOptions: [
      "featured",
      "lowest-premium-full",
      "lowest-premium-liability",
      "highest-rated",
    ],
    columns: ["carrier", "premiumFull", "premiumLiability", "highlight", "rating", "cta"],
    ctaLabel: "Get My Quote",
    trustLine: "Free quote · No spam · 2-min compare",
    headerLabel: "Auto insurance carriers · 2024 national avg premiums",
  },

  // Modal category routing (was PartnerRateTable.CATEGORY_TO_MODAL.insurance)
  modalCategory: "insurance",

  // Weekly-shoppers social-proof anchor
  // (was PartnerRateTable.SHOPPERS_THIS_WEEK_BY_CATEGORY.insurance)
  weeklyShoppersBenchmark: 2400,

  // Editorial sub-score field labels (was PartnerRateTable.SCORE_FIELDS_BY_CATEGORY.insurance)
  scoreFields: [
    { key: "score_rates", label: "Pricing", weight: "40%" },
    { key: "score_terms", label: "Coverage options", weight: "25%" },
    { key: "score_application", label: "Quote process", weight: "20%" },
    { key: "score_support", label: "Claims handling", weight: "15%" },
  ],

  // Admin per-row details schema (was RatesPanel.DETAILS_FIELDS.insurance)
  adminDetailsFields: [
    { key: "coverage_type", label: "Coverage type", type: "text", placeholder: "e.g. Liability + Collision + Comp" },
    { key: "avg_monthly_premium", label: "Avg monthly premium ($)", type: "number" },
    { key: "deductible_default", label: "Default deductible ($)", type: "number" },
    { key: "claim_satisfaction_score", label: "Claim satisfaction (0–1000)", type: "number" },
    { key: "driver_fit", label: "Best driver fit", type: "text", placeholder: "e.g. Clean record, multi-policy" },
  ],

  // Admin table-header labels (was RatesPanel.ADMIN_CATEGORY.insurance)
  adminTableHeader: {
    pluralNoun: "carriers",
    numericA: "Rating",
    numericB: "Highlight",
  },

  // CTA vocabulary (single source of truth — ctaVocabulary.js exports the literal)
  primaryCta: CTA_INSURANCE,

  // Routes owned by this vertical (used by sitemap, redirect guard, and the
  // future Phase 5 router split). Strings only — no React Router patterns
  // here so this config stays import-safe in test/build contexts.
  routes: [
    "/auto-insurance",
    "/insurance",
    "/insurance/:state",
    "/auto-insurance/states",
    "/insurance/non-owner-car-insurance",
  ],

  // Allowed Schema.org JSON-LD @types emitted on insurance pages
  schemaTypes: ["Product", "Review", "FAQPage", "BreadcrumbList", "Article", "AggregateRating"],

  // Trust badges valid on insurance surfaces (cf. iter-185.AF count gate)
  trustBadges: ["amBest", "jdPower", "naic"],

  // Whether the vertical uses credit-tier-based UI in admin (used by
  // RatesGroup's `isCreditVertical` branch — insurance does NOT)
  isCreditVertical: false,
};
